/**
 * A single event chip — the FRAME only: geometry, lane layout, accent colors,
 * state classes and data attributes. WHAT is rendered inside is decided by
 * the per-kind content registry (see ./eventContent) driven by `event.kind`,
 * which the model computed once at index-build time. This file contains no
 * event-type conditionals and never needs to change for a new kind.
 *
 * Pure & memoized: receives only primitives + the stable MCEvent reference
 * from the index. No callbacks — clicks are handled via event delegation on
 * the body (data-event-id), so re-renders happen ONLY when the event object
 * or a highlight tier flag actually changes.
 */
import React from "react";
import { MCEvent, MCGridConfig } from "./model/types";
import { yForMin } from "./model/gridGeometry";
import { useTherapyColorMap } from "../../hooks/useTherapyColorMap";
import { getContrastTextColor } from "../../themes/colorUtils";
import { contentForKind } from "./eventContent";
import { wantsFullStrip } from "./eventContent/parts";

interface EventBlockProps {
  event: MCEvent;
  cfg: MCGridConfig;
  /** Tier 1: appointment belongs to the selected patient */
  tierPatient: boolean;
  /** Jump-target pulse (one-shot) */
  tierJump: boolean;
}

const LANE_GAP_PX = 2;

const EventBlock: React.FC<EventBlockProps> = ({ event, cfg, tierPatient, tierJump }) => {
  // Catalog-sourced colour map (24h-cached reference data), theme fallback.
  const therapyColorMap = useTherapyColorMap();
  const top = yForMin(cfg, event.startMin);
  const height = Math.max(yForMin(cfg, event.endMin) - top - 1, cfg.slotHeightPx - 1);
  const durationMin = event.endMin - event.startMin;
  const isCompact = durationMin <= cfg.slotMin * 3;

  const laneFraction = 1 / event.laneCount;
  const left = `${event.lane * laneFraction * 100}%`;
  const width = `calc(${laneFraction * 100}% - ${LANE_GAP_PX}px)`;

  // MD-232: keyed by catalog identity, never by the rendered abbreviation —
  // an admin renaming a Heilmittel must not change what colour it draws.
  const accent = event.therapyColorKey
    ? therapyColorMap[event.therapyColorKey] || "grey"
    : undefined;
  const chipBg = accent && accent !== "grey" ? accent : "#d9dfe2";

  // `has-subloc` reserves right padding for the full strip; the predicate is
  // shared with the appointment content body, so padding and strip agree.
  const hasFullStrip = wantsFullStrip(event, isCompact);

  const className = [
    "mc-event",
    `mc-event--${event.kind}`,
    isCompact ? "is-compact" : "",
    hasFullStrip ? "has-subloc" : "",
    event.isCancelled ? "is-cancelled" : "",
    event.isDraft ? "is-draft" : "",
    event.isGroupTherapist ? "is-group" : "",
    event.isContinuous ? "is-continuous" : "",
    tierPatient ? "tier-patient" : "",
    tierJump ? "tier-jump" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties & Record<string, string | number> = {
    top,
    height,
    left,
    width,
    "--ev-accent": accent ?? "#6fa4bf",
    "--ev-chip-bg": chipBg,
    "--ev-chip-fg": getContrastTextColor(chipBg),
  };

  const Content = contentForKind(event.kind);

  return (
    <div
      className={className}
      style={style}
      data-event-id={event.id}
      data-appointment-id={event.appointmentId ?? undefined}
      title={`${event.startLabel}–${event.endLabel} ${event.title}`}
    >
      <Content event={event} isCompact={isCompact} />
    </div>
  );
};

export default React.memo(EventBlock);
